/**
 * Queues up task functions that return promises. Ensures only one
 * task is running at a time and that when the currently running
 * task completes, the next task is run. When adding a new task,
 * if a task is currently running, currently queued task is dropped.
 */
export class RunLatestQueue {
  /**
   * @type {boolean} Whether a task is currently running
   * @private
   */
  running = false;

  /**
   * @type {null | (() => Promise<any>)}
   * @private
   */
  next = null;

  /**
   * @type {null | ReturnType<typeof setTimeout>}
   * @private
   */
  timer = null;

  /**
   * @param {number} ms
   */
  constructor(ms = 200) {
    /**
     * @private
     * @type {number}
     */
    this.ms = ms;
  }

  /**
   * Add a task & start running if not currently running.
   *
   * @param {() => Promise<any>} task Task to queue or run.
   */
  add(task) {
    this.next = task;
    this.enqueue();
  }

  /**
   * @private
   */
  enqueue() {
    if (this.running) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      this.run();
    }, this.ms);
  }

  /**
   * Run the next task in the queue.
   *
   * @returns Promise<void>
   * @private
   */
  run() {
    if (!this.next) return;
    this.running = true;
    const { next } = this;
    this.next = null;
    const callback = () => {
      this.running = false;
      this.enqueue();
    };
    next().then(callback, callback);
  }
}
